import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { DatePicker, Select } from "antd";
import CommonSelect from "../../../core/common/commonSelect";
import {
  AdmissionNumber,
  classSectionId,
  RollNumber,
  studentclass,
  studentName,
} from "../../../core/common/selectoption/selectoption";
import { TableData } from "../../../core/data/interface";
import { RootState } from "../../../core/data/redux/store";
import Table from "../../../core/common/dataTable/index";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import TooltipOption from "../../../core/common/tooltipOption";
import { useDispatch, useSelector } from "react-redux";
import { fetchattendancesSuccess } from "../../../core/data/redux/attendanceSlice";
import { fetchmealsSuccess } from "../../../core/data/redux/mealSlice";
import {
  setAttendanceApi,
  updateAttendanceIndividualApi,
  checkAttendanceExistsApi,
} from "../../../core/data/services/attendanceService";
import { fetchStudentsSuccess } from "../../../core/data/redux/studentSlice";
import { fetchStudentsNewApi } from "../../../core/data/services/studentService";
import { fetchMealsApi, checkMealSubmissionExistsApi } from "../../../core/data/services/mealService";
import { fetchusersSuccess } from "../../../core/data/redux/userSlice";
import SuccessModal from '../../../core/common/successModal';
import ErrorModal from '../../../core/common/ErrorModal';
import {
  exportToPDF,
  exportToExcel,
  ExportColumn,
} from "../../../utils/exportUtils"
import CustomRadio from "../../../core/common/commonRadio";

const { Option } = Select;

const StudentAttendance = () => {
  const routes = all_routes;
  const dispatch = useDispatch();
  const currentDate = new Date().toISOString().split("T")[0];
  const [updatedAttendance, setUpdatedAttendance] = useState<any>([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);

  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Submission modal states
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [submissionData, setSubmissionData] = useState({
    remark: '',
    fileReference: '',
    selectedFile: null as File | null
  });

  // Attendance and meal states
  const [attendanceSubmitted, setAttendanceSubmitted] = useState<boolean>(false);
  const [canEditAttendance, setCanEditAttendance] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [mealsSubmitted, setMealsSubmitted] = useState<boolean>(false);
  const [mealReasonRecorded, setMealReasonRecorded] = useState<boolean>(false); // NEW: Track meal reason
  const [existingAttendanceData, setExistingAttendanceData] = useState<any[]>([]);

  const attendanceOptions = useMemo(() => [
    { id: 1, label: "Present" },
    { id: 2, label: "Absent" },
    { id: 3, label: "On Duty" },
  ], []);

  // Redux selectors
  const user = useSelector((state: RootState) => state.user.Users);
  const students = useSelector((state: RootState) => state.students.students);
  const attendances = useSelector((state: RootState) => state.attendance.attendances);

  // State for selected class and section
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedClassSectionId, setSelectedClassSectionId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<any>(currentDate);

  // Load user data from localStorage if not in Redux
  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    const userDetail = localStorage.getItem('userDetail');

    if (accessToken && (!user || user.length === 0) && userDetail) {
      try {
        const parsedUserDetail = JSON.parse(userDetail);
        dispatch(fetchusersSuccess([parsedUserDetail]));
      } catch (error) {
        console.error('Error parsing userDetail from localStorage:', error);
      }
    }
  }, [dispatch, user]);

  // Group sections by class
  const groupedClassSections = useMemo(() => {
    if (!user || !user[0]?.relatedEntity?.schoolClasses) return {};

    const sections = (user[0].relatedEntity.schoolClasses as unknown as any[])
      .reduce((acc: any[], schoolClassObj: any) => {
        if (Array.isArray(schoolClassObj.classSection)) {
          const sections = schoolClassObj.classSection.map((section: any) => ({
            className: schoolClassObj?.schoolClass?.name || schoolClassObj?.name,
            sectionName: section?.name,
            classSectionId: section?.id,
          }));
          return [...acc, ...sections];
        }
        return acc;
      }, []);

    return sections.reduce((grouped: any, section: any) => {
      const { className } = section;
      if (!grouped[className]) {
        grouped[className] = [];
      }
      grouped[className].push(section);
      return grouped;
    }, {});
  }, [user]);

  // Get unique class names for dropdown
  const classNames = useMemo(() => Object.keys(groupedClassSections), [groupedClassSections]);

  // Get sections for selected class
  const sectionsForSelectedClass = useMemo(() => {
    if (!selectedClass || !groupedClassSections[selectedClass]) return [];
    return groupedClassSections[selectedClass];
  }, [selectedClass, groupedClassSections]);

  // Get school ID with fallback
  const getSchoolId = useCallback(() => {
    let schoolId = user?.[0]?.relatedEntity?.schools?.id;

    if (!schoolId) {
      try {
        const userDetail = localStorage.getItem('userDetail');
        if (userDetail) {
          const parsedUserDetail = JSON.parse(userDetail);
          schoolId = parsedUserDetail?.relatedEntity?.schools?.id;
        }
      } catch (error) {
        console.error('Error getting school ID from localStorage:', error);
      }
    }

    return schoolId;
  }, [user]);

  // Clear data when switching sections
  const clearSectionData = useCallback(() => {
    setUpdatedAttendance([]);
    dispatch(fetchStudentsSuccess([]));
    dispatch(fetchattendancesSuccess([]));
    dispatch(fetchmealsSuccess([]));
    setAttendanceSubmitted(false);
    setCanEditAttendance(false);
    setMealsSubmitted(false);
    setMealReasonRecorded(false); // Reset meal reason state
    setIsEditMode(false);
    setExistingAttendanceData([]);
  }, [dispatch]);

  // Fetch students by classSectionId - with better error handling
  const fetchStudentsBySectionId = useCallback(async (
    classSectionId: number,
    selectedDate: string
  ) => {
    setIsLoadingStudents(true);
    try {
      const studentData = await fetchStudentsNewApi({
        classSectionId: classSectionId,
        attnDate: selectedDate,
      });

      // Filter students by classSectionId as an extra safety check
      const filteredStudents = studentData?.filter((student: any) =>
        student.classSectionId === classSectionId
      ) || [];

      let manipulatedData = filteredStudents.map((student: any) => ({
        ...student,
        attnTypeId: student?.attnTypeId || 1,
        studentId: student?.id,
        classSectionId: student.classSectionId
      }));

      // Update both local state and Redux
      setUpdatedAttendance(manipulatedData);
      dispatch(fetchStudentsSuccess(manipulatedData));

    } catch (error: any) {
      // Handle empty results gracefully - don't show error for empty sections
      dispatch(fetchStudentsSuccess([]));
      setUpdatedAttendance([]);

      // Only show error for actual API failures, not empty results
      if (error.message !== 'Student not found' && !error.message.includes('not found')) {
        setShowError(true);
        setErrorMessage(`Failed to fetch students: ${ error.message }`);
      }
    } finally {
      setIsLoadingStudents(false);
    }
  }, [dispatch]);

  // Fetch attendance by classSectionId with edit capability check
  const fetchAttendanceBySectionId = useCallback(async (
    classSectionId: number,
    selectedDate: string
  ) => {
    try {
      const schoolId = getSchoolId();

      const attendanceStatus = await checkAttendanceExistsApi({
        classSectionId: classSectionId,
        attnDate: selectedDate,
        schoolId: schoolId
      });

      dispatch(fetchattendancesSuccess(attendanceStatus.data));
      setExistingAttendanceData(attendanceStatus.data);
      setAttendanceSubmitted(attendanceStatus.exists);
      setCanEditAttendance(attendanceStatus.canEdit);

    } catch (error: any) {
      dispatch(fetchattendancesSuccess([]));
      setExistingAttendanceData([]);
      setAttendanceSubmitted(false);
      setCanEditAttendance(false);
      console.error("Error fetching attendance:", error.message);
    }
  }, [dispatch, getSchoolId]);

  // ENHANCED: Fetch meals by classSectionId to check if meals are submitted OR meal reason is recorded
  const fetchMealsBySectionId = useCallback(async (
    classSectionId: number,
    selectedDate: string
  ) => {
    try {
      const schoolId = getSchoolId();

      // Check meal submission status
      const mealStatus = await checkMealSubmissionExistsApi({
        classSectionId: classSectionId,
        mealDate: selectedDate,
        schoolId: schoolId
      });

      setMealsSubmitted(mealStatus.exists);

      // ENHANCED: Check if meal reason is recorded (this blocks attendance editing)
      if (mealStatus.exists && mealStatus.data && mealStatus.data.mealNotServedReasonId) {
        setMealReasonRecorded(true);
      } else {
        setMealReasonRecorded(false);
      }

      // Also fetch meal data for display
      const mealData = await fetchMealsApi({
        classSectionId: classSectionId,
        mealDate: selectedDate,
        limit: 1000
      });

      dispatch(fetchmealsSuccess(mealData));
    } catch (error: any) {
      dispatch(fetchmealsSuccess([]));
      setMealsSubmitted(false);
      setMealReasonRecorded(false);
      console.error("Error fetching meals:", error.message);
    }
  }, [dispatch, getSchoolId]);

  // Only merge data when both students and attendance are for the same section
  useEffect(() => {
    if (Array.isArray(students) && Array.isArray(attendances) && selectedClassSectionId) {
      // Filter students to ensure they belong to the selected section
      const sectionStudents = students.filter((student: any) =>
        student.classSectionId === selectedClassSectionId
      );

      const mergedData = sectionStudents.map((student: any) => {
        const matchedAttendance = attendances.find((att: any) => att.studentId === student.id);

        // ENHANCED: Consider meal submission and meal reason when determining if attendance is editable
        const attendanceEditable = (canEditAttendance || !attendanceSubmitted) && !mealsSubmitted && !mealReasonRecorded;

        return {
          ...student,
          attnTypeId: matchedAttendance?.attnTypeId || 1,
          attendanceEditable: attendanceEditable,
          attendanceId: matchedAttendance?.id,
          student: student.student,
          id: student.id,
          studentId: student.id,
          classSectionId: student.classSectionId
        };
      });

      setUpdatedAttendance(mergedData);
    }
  }, [students, attendances, attendanceSubmitted, canEditAttendance, selectedClassSectionId, mealsSubmitted, mealReasonRecorded]);

  // Handle file selection
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // For now, we'll just store the file name
      // In a real app, you'd upload the file and get a URL
      setSubmissionData(prev => ({
        ...prev,
        selectedFile: file,
        fileReference: file.name // Use file name as reference for now
      }));
    } else {
      setSubmissionData(prev => ({
        ...prev,
        selectedFile: null,
        fileReference: ''
      }));
    }
  }, []);

  // ENHANCED: Submit or update attendance with meal validation
  const handleSubmitClick = useCallback(() => {
    if (!Array.isArray(updatedAttendance) || updatedAttendance.length === 0) {
      setShowError(true);
      setErrorMessage('No attendance data to submit.');
      return;
    }

    if (!selectedClassSectionId) {
      setShowError(true);
      setErrorMessage('Please select a class section.');
      return;
    }

    // ENHANCED: Check if meals are submitted OR meal reason is recorded (both block attendance editing)
    if (attendanceSubmitted && (mealsSubmitted || mealReasonRecorded)) {
      setShowError(true);
      if (mealReasonRecorded) {
        setErrorMessage('Cannot edit attendance after a meal reason has been recorded.');
      } else {
        setErrorMessage('Cannot edit attendance after meals have been submitted.');
      }
      return;
    }

    // Check if attendance cannot be edited
    if (attendanceSubmitted && !canEditAttendance) {
      setShowError(true);
      setErrorMessage('Attendance has already been edited and cannot be modified further.');
      return;
    }

    // Verify all students belong to the selected section
    const invalidStudents = updatedAttendance.filter((student: any) =>
      student.classSectionId !== selectedClassSectionId
    );

    if (invalidStudents.length > 0) {
      setShowError(true);
      setErrorMessage('Data inconsistency detected. Please refresh and try again.');
      return;
    }

    const schoolId = getSchoolId();

    if (!schoolId) {
      setShowError(true);
      setErrorMessage('School information not found. Please check your user profile or try refreshing the page.');
      return;
    }

    // Set edit mode if attendance already exists
    setIsEditMode(attendanceSubmitted && canEditAttendance);
    setShowSubmissionModal(true);
  }, [updatedAttendance, selectedClassSectionId, attendanceSubmitted, canEditAttendance, mealsSubmitted, mealReasonRecorded, getSchoolId]);

  const submitAttendance = useCallback(async () => {
    setIsSubmitting(true);
    setShowSubmissionModal(false);

    try {
      const schoolId = getSchoolId();

      if (!schoolId) {
        throw new Error('School information not found');
      }

      // Create proper fileReference object
      const createFileReference = () => {
        if (submissionData.fileReference && submissionData.fileReference.trim()) {
          return { url: submissionData.fileReference.trim() };
        }
        return { url: "https://example.com/default-file.jpg" };
      };

      if (isEditMode) {
        // Update existing attendance records individually
        const updatePromises = updatedAttendance.map(async (student: any) => {
          const existingRecord = existingAttendanceData.find((att: any) => att.studentId === student.id);

          if (!existingRecord?.id) {
            throw new Error(`No existing attendance record found for student ${ student.id }`);
          }

          const updateData = {
            attendance: student?.attnTypeId === 1 || student?.attnTypeId === 3,
            attnTypeId: parseInt(student?.attnTypeId?.toString() || "1"),
            recordedVia: 1,
            fileReference: createFileReference(),
            updatedBy: parseInt(user?.[0]?.id?.toString() || "3"),
          };

          console.log('Update data for student:', student.id, updateData);
          return updateAttendanceIndividualApi(existingRecord.id, updateData);
        });

        await Promise.all(updatePromises);
        setSuccessMessage('Attendance updated successfully!');
      } else {
        // Prepare attendance data for all students (new submission)
        const attendancePayload = updatedAttendance.map((student: any) => {
          const payload = {
            studentId: parseInt(student?.id?.toString() || "0"),
            schoolId: parseInt(schoolId?.toString() || "0"),
            classSectionId: parseInt(selectedClassSectionId?.toString() || "0"),
            attnDate: selectedDate,
            attendance: student?.attnTypeId === 1,
            attnTypeId: parseInt(student?.attnTypeId?.toString() || "1"),
            recordedVia: 1,
            fileReference: createFileReference(), // Always an object
            createdBy: parseInt(user?.[0]?.id?.toString() || "3"),
          };

          return payload;
        });

        console.log('Final payload being sent:', JSON.stringify({ attendances: attendancePayload }, null, 2));

        await setAttendanceApi({
          attendances: attendancePayload,
        });

        setSuccessMessage('Attendance submitted successfully!');
      }

      // Refresh attendance data after successful submission/update
      if (selectedClassSectionId) {
        await fetchAttendanceBySectionId(selectedClassSectionId, selectedDate);
        await fetchStudentsBySectionId(selectedClassSectionId, selectedDate);
        await fetchMealsBySectionId(selectedClassSectionId, selectedDate);
      }

      setShowSuccess(true);

      // Reset submission data and edit mode
      setSubmissionData({ remark: '', fileReference: '', selectedFile: null });
      setIsEditMode(false);

    } catch (error: any) {
      console.error("Error submitting/updating attendance:", error);
      setShowError(true);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to submit attendance.';
      setErrorMessage(`Failed to ${ isEditMode ? 'update' : 'submit' } attendance: ${ errorMessage }`);
    } finally {
      setIsSubmitting(false);
    }
  }, [updatedAttendance, user, selectedClassSectionId, selectedDate, existingAttendanceData, fetchAttendanceBySectionId, fetchStudentsBySectionId, fetchMealsBySectionId, submissionData, getSchoolId, isEditMode]);

  // Initialize with first class and first section
  useEffect(() => {
    if (classNames.length > 0 && !selectedClass) {
      const firstClass = classNames[0];
      setSelectedClass(firstClass);

      if (groupedClassSections[firstClass] && groupedClassSections[firstClass].length > 0) {
        const firstSection = groupedClassSections[firstClass][0];
        setSelectedClassSectionId(firstSection.classSectionId);
        fetchAttendanceBySectionId(firstSection.classSectionId, selectedDate);
        fetchStudentsBySectionId(firstSection.classSectionId, selectedDate);
        fetchMealsBySectionId(firstSection.classSectionId, selectedDate);
      }
    }
  }, [classNames, selectedClass, groupedClassSections, selectedDate, fetchAttendanceBySectionId, fetchStudentsBySectionId, fetchMealsBySectionId]);

  // ENHANCED: Filter table data by selected section with meal validation
  const tableData = useMemo(() => {
    if (!Array.isArray(students) || !selectedClassSectionId) return [];

    // Filter students by the currently selected section
    const sectionStudents = students.filter((student: any) =>
      student.classSectionId === selectedClassSectionId
    );

    return sectionStudents.map((item: any, idx: number) => {
      const studentId = item.id;

      const matchedAttendance = Array.isArray(attendances)
        ? attendances.find((x: any) => x?.studentId === studentId)
        : null;

      // ENHANCED: Determine if attendance can be edited (consider meal reason as well)
      const canEdit = (canEditAttendance || !attendanceSubmitted) && !mealsSubmitted && !mealReasonRecorded;

      return {
        key: studentId || idx,
        rollNo: item?.student?.rollNo || item?.rollNo || "",
        firstName: item?.student?.firstName || item?.firstName || "",
        attendance: matchedAttendance?.attnTypeId || 1,
        attendanceEditable: canEdit,
        mealsServed: mealsSubmitted,
        mealReasonRecorded: mealReasonRecorded, // NEW: Track meal reason status
        classSectionId: item.classSectionId,
        ...item,
      };
    });
  }, [students, attendances, attendanceSubmitted, canEditAttendance, mealsSubmitted, mealReasonRecorded, selectedClassSectionId]);

  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  // Clear data when changing class
  const handleClassChange = useCallback((className: string) => {
    clearSectionData();
    setSelectedClass(className);
    setSelectedClassSectionId(null);

    if (groupedClassSections[className] && groupedClassSections[className].length > 0) {
      const firstSection = groupedClassSections[className][0];
      setSelectedClassSectionId(firstSection.classSectionId);
      fetchAttendanceBySectionId(firstSection.classSectionId, selectedDate);
      fetchStudentsBySectionId(firstSection.classSectionId, selectedDate);
      fetchMealsBySectionId(firstSection.classSectionId, selectedDate);
    }
  }, [groupedClassSections, selectedDate, fetchAttendanceBySectionId, fetchStudentsBySectionId, fetchMealsBySectionId, clearSectionData]);

  // Clear data when changing section
  const handleSectionClick = useCallback((classSectionId: number) => {
    if (classSectionId === selectedClassSectionId) return;

    clearSectionData();
    setSelectedClassSectionId(classSectionId);

    fetchAttendanceBySectionId(classSectionId, selectedDate);
    fetchStudentsBySectionId(classSectionId, selectedDate);
    fetchMealsBySectionId(classSectionId, selectedDate);
  }, [selectedDate, selectedClassSectionId, fetchAttendanceBySectionId, fetchStudentsBySectionId, fetchMealsBySectionId, clearSectionData]);

  // Handle state change for each row
  const handleOptionChange = (
    index: number,
    studentId: number,
    value: string
  ) => {
    setUpdatedAttendance((prev: any[]) => {
      const updated = [...prev];
      const studentIndex = updated.findIndex((student: any) => student.id === studentId);
      if (studentIndex !== -1) {
        updated[studentIndex] = {
          ...updated[studentIndex],
          attnTypeId: parseInt(value)
        };
      }
      return updated;
    });
  };

  const columns = [
    {
      title: "Roll No",
      dataIndex: "rollNo",
      sorter: (a: TableData, b: TableData) => a.rollNo.length - b.rollNo.length,
    },
    {
      title: "Name",
      dataIndex: "firstName",
      render: (text: string, record: any) => (
        <div className="d-flex align-items-center">
          <Link to="#" className="avatar avatar-md">
            <ImageWithBasePath
              src={record.img || record.profileImage || "assets/img/teachers/teacher-01.jpg"}
              className="img-fluid rounded-circle"
              alt="img"
            />
          </Link>
          <div className="ms-2">
            <p className="text-dark mb-0">
              <Link to="#">{text}</Link>
            </p>
          </div>
        </div>
      ),
      sorter: (a: TableData, b: TableData) => a.name?.length - b.name?.length,
    },
    {
      title: "Attendance",
      dataIndex: "attendance",
      render: (text: string, record: any, index: number) => {
        const selectedAttendance = updatedAttendance?.find((x: any) => x?.id === record?.id)?.attnTypeId ?? record?.attnTypeId ?? 1;
        const isDisabled = !record?.attendanceEditable;

        return (
          <div className="d-flex align-items-center check-radio-group flex-nowrap">
            {attendanceOptions?.map((option: any) => (
              <CustomRadio
                key={option?.id}
                id={`attendance-${ index }-${ option?.id }`}
                name={`attendance-${ index }`}
                className={`custom-radio ${ isDisabled ? 'disabled' : '' }`}
                checked={selectedAttendance === option?.id}
                value={true}
                label={option?.label ?? ''}
                onChange={() => !isDisabled && handleOptionChange(index, record?.id, option?.id)}
              />
               ))}
          </div>
        );
      },
      sorter: (a: TableData, b: TableData) =>
        a.attendance.length - b.attendance.length,
    },
  ];

  // ENHANCED: Determine button state and text with meal reason consideration
  const getButtonState = () => {
    if (isSubmitting || isLoadingStudents) {
      return {
        disabled: true,
        className: 'btn-secondary',
        icon: 'ti-loader-2 animate-spin',
        text: isSubmitting ? 'Processing...' : 'Loading...'
      };
    }

    if (tableData.length === 0) {
      return {
        disabled: true,
        className: 'btn-secondary',
        icon: 'ti-users-off',
        text: 'No Students Found'
      };
    }

    // ENHANCED: Block attendance editing if meal reason is recorded
    if (attendanceSubmitted && mealReasonRecorded) {
      return {
        disabled: true,
        className: 'btn-danger',
        icon: 'ti-lock',
        text: 'Attendance Locked (Meal Reason Recorded)'
      };
    }

    if (attendanceSubmitted && mealsSubmitted) {
      return {
        disabled: true,
        className: 'btn-success',
        icon: 'ti-lock',
        text: 'Attendance Locked (Meals Submitted)'
      };
    }

    if (attendanceSubmitted && !canEditAttendance) {
      return {
        disabled: true,
        className: 'btn-success',
        icon: 'ti-lock',
        text: 'Attendance Locked (Already Edited)'
      };
    }

    if (attendanceSubmitted && canEditAttendance && !mealsSubmitted && !mealReasonRecorded) {
      return {
        disabled: false,
        className: 'btn-warning',
        icon: 'ti-edit',
        text: 'Edit Attendance (Once Only)'
      };
    }

    return {
      disabled: false,
      className: 'btn-primary',
      icon: 'ti-square-rounded-plus',
      text: 'Submit Attendance'
    };
  };

  const buttonState = getButtonState();

  const exportData = useMemo(() => {
    return updatedAttendance.map((student: any) => {
      const attendanceMap = {
        1: "Present",
        2: "Absent",
        3: "On Duty",
      };

      return {
        rollNo: student?.rollNo || student?.student?.rollNo || "",
        firstName: student?.firstName || student?.student?.firstName || "",
        lastName: student?.lastName || student?.student?.lastName || "",
        attendanceLabel: attendanceMap[student?.attnTypeId as keyof typeof attendanceMap] || "Present",
        schoolClass: student?.schoolClass || "",
        classSection: student?.classSection || "",
      };
    });
  }, [updatedAttendance]);

  const exportColumns: ExportColumn[] = [
    { title: "Roll No", dataIndex: "rollNo" },
    {
      title: "Full Name",
      dataIndex: "firstName",
      render: (_, row) => `${row.firstName} ${row.lastName}`,
    },
    { title: "Attendance", dataIndex: "attendanceLabel" },
    {
      title: "Class & Section",
      dataIndex: "schoolClassId",
      render: (_, row) => `${row.schoolClass}-${row.classSection}`,
    },
  ];

  const handleExportPDF = () => {
    exportToPDF(exportData, exportColumns, "Student-Attendance.pdf");
  };

  const handleExportExcel = () => {
    exportToExcel(exportData, exportColumns, "Student-Attendance.xlsx");
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Student Attendance</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.teacherDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.reportMeals}>Report</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Student Attendance
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
              />

              {/* Submit/Edit button */}
              <div className="mb-2">
                <button
                  type="button"
                  className={`btn d-flex align-items-center ${buttonState.className}`}
                  onClick={handleSubmitClick}
                  disabled={buttonState.disabled}
                >
                  <i className={`ti ${buttonState.icon} me-2`} />
                  {buttonState.text}
                </button>
              </div>
            </div>
          </div>

          <div className="row mb-3 align-items-stretch">
            {/* Class Selection */}
            <div className="col-md-2 col-lg-2">
              <div className="form-group h-100 d-flex flex-column">
                <label htmlFor="classSelect" className="form-label mb-2">Select Class</label>
                <div className="flex-grow-1 d-flex align-items-start">
                  <Select
                    id="classSelect"
                    placeholder="Select a class"
                    value={selectedClass}
                    onChange={handleClassChange}
                    className="w-100"
                    size="large"
                    style={{ minHeight: '40px' }}
                    loading={isLoadingStudents}
                  >
                    {classNames.map((className: string) => (
                      <Option key={className} value={className}>
                        {className}
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            {/* Sections Selection */}
            <div className="col-md-10 col-lg-10">
              {selectedClass && sectionsForSelectedClass.length > 0 ? (
                <div className="form-group h-100 d-flex flex-column">
                  <label className="form-label mb-2">
                    Sections for {selectedClass}
                  </label>
                  <div className="flex-grow-1">
                    <div
                      className="d-flex gap-2 pb-2 h-100 align-items-start"
                      style={{
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#888 #f1f1f1',
                        minHeight: '60px'
                      }}
                    >
                      {sectionsForSelectedClass.map((section: any, idx: number) => (
                        <div
                          key={idx}
                          className={`card flex-shrink-0 ${ selectedClassSectionId === section.classSectionId ? "border-primary bg-primary text-white" : "border-secondary"
                            }`}
                          style={{
                            cursor: "pointer",
                            minWidth: "100px",
                            width: "100px",
                            height: "40px",
                            transition: "all 0.2s ease"
                          }}
                          onClick={() => handleSectionClick(section.classSectionId)}
                        >
                          <div className="card-body d-flex flex-column justify-content-center align-items-center p-2 h-100">
                            <div className="fw-bold text-center" style={{ fontSize: '0.9rem', lineHeight: '1.2' }}>
                              {section.sectionName}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : selectedClass ? (
                <div className="form-group h-100 d-flex flex-column">
                  <label className="form-label mb-2">Sections</label>
                  <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                    <div className="text-muted">No sections available for {selectedClass}</div>
                  </div>
                </div>
              ) : (
                <div className="form-group h-100 d-flex flex-column">
                  <label className="form-label mb-2">Sections</label>
                  <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                    <div className="text-muted">Please select a class first</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Student List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">
                Student Attendance List
              </h4>
              <div className="d-flex align-items-center flex-wrap">
                {/* <div className="input-icon-start mb-3 me-2 position-relative">
                  <div className="date-pic">
                    <DatePicker
                      className="form-control datetimepicker"
                      format={{
                        format: "YYYY-MM-DD",
                        type: "mask",
                      }}
                      placeholder={selectedDate}
                      onChange={(date, dateString) => {
                        const dateStr = Array.isArray(dateString)
                          ? dateString[0]
                          : dateString;
                        setSelectedDate(dateStr);
                        if (selectedClassSectionId && dateStr) {
                          clearSectionData();
                          fetchAttendanceBySectionId(selectedClassSectionId, dateStr);
                          fetchStudentsBySectionId(selectedClassSectionId, dateStr);
                          fetchMealsBySectionId(selectedClassSectionId, dateStr);
                        }
                      }}
                    />
                    <span className="cal-icon">
                      <i className="ti ti-calendar" />
                    </span>
                  </div>
                </div> */}
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Admission No</label>
                              <CommonSelect
                                className="select"
                                options={AdmissionNumber}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Roll No</label>
                              <CommonSelect
                                className="select"
                                options={RollNumber}
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Name</label>
                              <CommonSelect
                                className="select"
                                options={studentName}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-0">
                              <label className="form-label">Class</label>
                              <CommonSelect
                                className="select"
                                options={studentclass}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-0">
                              <label className="form-label">Section</label>
                              <CommonSelect
                                className="select"
                                options={classSectionId}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link to="#" className="btn btn-light me-3">
                          Reset
                        </Link>
                        <Link to="#" className="btn btn-primary">
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              {/* ENHANCED: Show locked attendance message for meal reason */}
              {!isLoadingStudents && tableData.length > 0 && attendanceSubmitted && mealReasonRecorded && (
                <div className="alert alert-danger mt-3 mx-3">
                  <div className="d-flex align-items-center">
                    <i className="ti ti-ban me-2" style={{ fontSize: '1.5rem', color: '#dc3545' }}></i>
                    <div>
                      <h6 className="mb-1" style={{ color: '#721c24' }}>Attendance Locked - Meal Reason Recorded</h6>
                      <p className="mb-0" style={{ color: '#721c24' }}>
                        Attendance for <strong>{selectedDate}</strong> cannot be edited because a reason for not providing meals has been recorded.
                        Please remove the meal reason if you need to edit attendance.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Show locked attendance message for meals submitted */}
              {!isLoadingStudents && tableData.length > 0 && attendanceSubmitted && mealsSubmitted && !mealReasonRecorded && (
                <div className="alert alert-secondary mt-3 mx-3">
                  <div className="d-flex align-items-center">
                    <i className="ti ti-check-circle me-2" style={{ fontSize: '1.5rem' }}></i>
                    <div>
                      <h6 className="mb-1">Attendance Locked - Meals Submitted</h6>
                      <p className="mb-0">
                        Attendance for <strong>{selectedDate}</strong> is locked because meals have been submitted.
                        No further modifications are allowed.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Show locked attendance message for already edited */}
              {!isLoadingStudents && tableData.length > 0 && attendanceSubmitted && !canEditAttendance && !mealsSubmitted && !mealReasonRecorded && (
                <div className="alert alert-secondary mt-3 mx-3">
                  <div className="d-flex align-items-center">
                    <i className="ti ti-check-circle me-2" style={{ fontSize: '1.5rem' }}></i>
                    <div>
                      <h6 className="mb-1">Attendance Locked</h6>
                      <p className="mb-0">
                        Attendance for <strong>{selectedDate}</strong> has been edited once and is now locked.
                        No further modifications are allowed.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Show editable attendance message */}
              {!isLoadingStudents && tableData.length > 0 && attendanceSubmitted && canEditAttendance && !mealsSubmitted && !mealReasonRecorded && (
                <div className="alert alert-warning mt-3 mx-3">
                  <div className="d-flex align-items-center">
                    <i className="ti ti-edit me-2" style={{ fontSize: '1.5rem', color: '#856404' }}></i>
                    <div>
                      <h6 className="mb-1" style={{ color: '#856404' }}>Attendance Can Be Edited Once</h6>
                      <p className="mb-0" style={{ color: '#856404' }}>
                        Attendance for <strong>{selectedDate}</strong> has been submitted but can be edited once more.
                        After editing or when meals are submitted, it will be locked permanently.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {isLoadingStudents ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading students...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading students for selected section...</p>
                </div>
              ) : tableData.length === 0 ? (
                <div className="text-center py-5">
                  <i className="ti ti-users-off text-muted" style={{ fontSize: '3rem' }}></i>
                  <h5 className="mt-3 text-muted">No Students Found</h5>
                  <p className="text-muted">
                    {selectedClassSectionId
                      ? `No students found in section ID: ${selectedClassSectionId}. This section may be empty or not have any enrolled students.`
                      : 'Please select a class and section to view students'
                    }
                  </p>
                  <div className="mt-3">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        if (selectedClassSectionId) {
                          clearSectionData();
                          fetchStudentsBySectionId(selectedClassSectionId, selectedDate);
                        }
                      }}
                    >
                      <i className="ti ti-refresh me-2"></i>
                      Refresh Data
                    </button>
                  </div>
                </div>
              ) : (
                <Table
                  dataSource={tableData}
                  columns={columns}
                  Selection={false}
                  onRow={(record: any) => ({
                    onClick: (event: React.MouseEvent) => {
                      if (!record?.attendanceEditable) {
                        event.stopPropagation();
                      }
                    },
                    style: !record?.attendanceEditable ? {
                      pointerEvents: 'none',
                      opacity: 0.7,
                      backgroundColor: '#f8f9fa',
                      color: '#6c757d'
                    } : {},
                  })}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Submission Modal */}
      {showSubmissionModal && (
        <div className="modal fade show d-block mt-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isEditMode ? 'Edit Attendance' : 'Submit Attendance'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSubmissionModal(false)}
                  disabled={isSubmitting}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-4">
                  <div className={`alert ${ isEditMode ? 'alert-warning' : 'alert-info' }`}>
                    <i className={`ti ${ isEditMode ? 'ti-edit' : 'ti-info-circle' } me-2`}></i>
                    You are about to {isEditMode ? 'edit' : 'submit'} attendance for{' '}
                    <strong>{updatedAttendance.length}</strong> students for <strong>{selectedDate}</strong>{' '}
                    in section <strong>{selectedClassSectionId}</strong>.
                    {isEditMode && (
                      <div className="mt-2 fw-bold">
                        ⚠️ This is your only edit opportunity. After this, attendance will be locked permanently.
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Remark <span className="text-muted">(Optional)</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={2}
                    placeholder={`Enter any remarks or notes about this attendance ${ isEditMode ? 'update' : 'submission' }...`}
                    value={submissionData.remark}
                    onChange={(e) => setSubmissionData(prev => ({ ...prev, remark: e.target.value }))}
                    disabled={isSubmitting}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    File Reference <span className="text-muted">(Optional)</span>
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    onChange={handleFileChange}
                    disabled={isSubmitting}
                    accept="image/*,.pdf,.doc,.docx"
                  />
                  {submissionData.selectedFile && (
                    <small className="text-muted mt-1 d-block">
                      Selected: {submissionData.selectedFile.name}
                    </small>
                  )}
                </div>

                <div className="mb-3">
                  <div className="card bg-light">
                    <div className="card-body p-3">
                      <h6 className="card-title mb-2">
                        {isEditMode ? 'Update' : 'Submission'} Details
                      </h6>
                      <div className="row text-sm">
                        <div className="col-6">
                          <strong>Date:</strong> {selectedDate}
                        </div>
                        <div className="col-6">
                          <strong>Students:</strong> {updatedAttendance.length}
                        </div>
                        <div className="col-6">
                          <strong>Class:</strong> {selectedClass}
                        </div>
                        <div className="col-6">
                          <strong>Section ID:</strong> {selectedClassSectionId}
                        </div>
                        <div className="col-12 mt-2">
                          <strong>School:</strong> {user?.[0]?.relatedEntity?.schools?.name || 'Not found'}
                          <span className="text-muted"> (ID: {getSchoolId() || 'Not found'})</span>
                        </div>
                        {isEditMode && (
                          <div className="col-12 mt-2">
                            <strong>Edit Status:</strong>{' '}
                            <span className="badge bg-warning">
                              Final Edit (Will Lock After)
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={() => setShowSubmissionModal(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn ${ isEditMode ? 'btn-warning' : 'btn-primary' }`}
                  onClick={submitAttendance}
                  disabled={isSubmitting || !getSchoolId()}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ti ti-loader-2 me-2 animate-spin"></i>
                      {isEditMode ? 'Updating...' : 'Submitting...'}
                    </>
                  ) : (
                    <>
                      <i className={`ti ${ isEditMode ? 'ti-edit' : 'ti-check' } me-2`}></i>
                      {isEditMode ? 'Confirm Edit' : 'Confirm Submit'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <SuccessModal
        show={showSuccess}
        message={successMessage}
        onClose={() => setShowSuccess(false)}
      />

      {/* Error Modal */}
      <ErrorModal
        show={showError}
        message={errorMessage}
        onClose={() => setShowError(false)}
      />
    </div>
  );
};

export default StudentAttendance;